import React, { type JSX } from "react";
import { /* MatrixError,*/ type Room, RoomMember } from "matrix-js-sdk/src/matrix";

import { logger } from "matrix-js-sdk/src/logger";
import { _t, _td } from "../../../languageHandler.tsx";
import { MatrixClientPeg } from "../../../MatrixClientPeg.ts";

import { type IInviteResult, inviteMultipleToRoom, showAnyInviteErrors } from "../../../RoomInvite.tsx";
// import SettingsStore from "../../../settings/SettingsStore.ts";
// import { UIFeature } from "../../../settings/UIFeature.ts";
import BaseDialog from "./BaseDialog.tsx";
// import { type ScreenName } from "../../../PosthogTrackers.ts";
import {
    DirectoryMember,
    type Member,
    startDmOnFirstMessage,
    // ThreepidMember,
} from "../../../utils/direct-messages.ts";
import { InviteKind } from "./InviteDialogTypes.ts";
// import Modal from "../../../Modal.tsx";
import { privateShouldBeEncrypted } from "../../../utils/rooms.ts";
import { getRoomDisplayName } from "../../../utils/event/eventUtils";
// import AskInviteAnywayDialog/*, { type UnknownProfiles }*/ from "./AskInviteAnywayDialog.tsx";
// import { SdkContextClass } from "../../../contexts/SDKContext.ts";
// import { type UserProfilesStore } from "../../../stores/UserProfilesStore.ts";
import InviteProgressBody from "./InviteProgressBody.tsx";
import { IDthContact } from "../../../dth/clientHttp/models/httpClientModels.ts";
import ContactList from "../dth_components/ContactList.tsx";
import DialogButtons from "../elements/DialogButtons.tsx";
// import { UNKNOWN_PROFILE_ERRORS } from "../../../utils/MultiInviter.ts";

// we have a number of types defined from the Matrix spec which can't reasonably be altered here.
/* eslint-disable camelcase */
// const extractTargetUnknownProfiles = async (
//     targets: Member[],
//     profilesStores: UserProfilesStore,
// ): Promise<UnknownProfiles> => {
//     const directoryMembers = targets.filter((t): t is DirectoryMember => t instanceof DirectoryMember);
//     await Promise.all(directoryMembers.map((t) => profilesStores.getOrFetchProfile(t.userId)));
//     return directoryMembers.reduce<UnknownProfiles>((unknownProfiles: UnknownProfiles, target: DirectoryMember) => {
//         const lookupError = profilesStores.getProfileLookupError(target.userId);
//         if (
//             lookupError instanceof MatrixError &&
//             lookupError.errcode &&
//             UNKNOWN_PROFILE_ERRORS.includes(lookupError.errcode)
//         ) {
//             unknownProfiles.push({
//                 userId: target.userId,
//                 errorText: lookupError.data.error || "",
//             });
//         }

//         return unknownProfiles;
//     }, []);
// };


interface BaseProps {
    // Takes a boolean which is true if a user / users were invited /
    // a call transfer was initiated or false if the dialog was cancelled
    // with no action taken.
    onFinished: (success?: boolean) => void;

    // Initial value to populate the filter with
    initialText?: string;
}

interface InviteDMProps extends BaseProps {
    // The kind of invite being performed. Assumed to be InviteKind.Dm if not provided.
    kind?: InviteKind.Dm;
}

interface InviteRoomProps extends BaseProps {
    kind: InviteKind.Invite;

    // The room ID this dialog is for. Only required for InviteKind.Invite.
    roomId: string;
}

function isRoomInvite(props: Props): props is InviteRoomProps {
    return props.kind === InviteKind.Invite;
}

type Props = InviteDMProps | InviteRoomProps;

interface IInviteDialogState {
    targets: Member[]; // array of Member objects (see interface above)
    filterText: string;
    // canUseIdentityServer: boolean;
    // tryingIdentityServer: boolean;

    contacts: IDthContact[],

    /**
     * True if we are sending the invites.
     *
     * We will grey out the action button, hide the suggestions, and display a spinner.
     */
    busy: boolean;

    /** Error from the last attempt to send invites. */
    errorText?: string;
}

export default class DthInviteDialog extends React.PureComponent<Props, IInviteDialogState> {
    public static defaultProps: Partial<Props> = {
        kind: InviteKind.Dm,
        initialText: "",
    };

    private encryptionByDefault = false;
    // private profilesStore: UserProfilesStore;

    public constructor(props: Props) {
        super(props);

        if (props.kind === InviteKind.Invite && !props.roomId) {
            throw new Error("When using InviteKind.Invite a roomId is required for an InviteDialog");
        }

        // this.profilesStore = SdkContextClass.instance.userProfilesStore;
        // const cli = MatrixClientPeg.safeGet();

        this.state = {
            targets: [], // array of Member objects (see interface above)
            filterText: this.props.initialText || "",
            // canUseIdentityServer: !!cli.getIdentityServerUrl(),
            // tryingIdentityServer: false,
            contacts: [],
            // These two flags are used for the 'Go' button to communicate what is going on.
            busy: false,
        };
    }

    public async componentDidMount(): Promise<void> {
        this.encryptionByDefault = privateShouldBeEncrypted(MatrixClientPeg.safeGet());
    }

    public componentDidUpdate(prevProps: Props): void {
        const prevIsRoom = isRoomInvite(prevProps);
        const nowIsRoom = isRoomInvite(this.props);

        const roomChanged =
            (prevIsRoom && nowIsRoom && (prevProps as InviteRoomProps).roomId !== (this.props as InviteRoomProps).roomId) ||
            (prevIsRoom !== nowIsRoom) ||
            prevProps.kind !== this.props.kind;

        if (roomChanged) {
            // Se vuoi, resetta anche i targets per sicurezza (evita invitare selezioni della stanza precedente)
            this.setState({ targets: [] });
        }
    }


    private shouldAbortAfterInviteError(result: IInviteResult, room: Room): boolean {
        this.setState({ busy: false });
        const userMap = new Map<string, Member>(this.state.targets.map((member) => [member.userId, member]));
        return !showAnyInviteErrors(result.states, room, result.inviter, userMap);
    }

    private convertFilter(): Member[] {
        // Check to see if there's anything to convert first
        if (!this.state.filterText || !this.state.filterText.includes("@")) return this.state.targets || [];

        if (!this.canInviteMore()) {
            return this.state.targets;
        }

        let newMember: Member | undefined;
        if (this.state.filterText.startsWith("@")) {
            // Assume mxid
            newMember = new DirectoryMember({ user_id: this.state.filterText });
        }
        // else if (SettingsStore.getValue(UIFeature.IdentityServer)) {
        //     // Assume email
        //     if (this.canInviteThirdParty()) {
        //         newMember = new ThreepidMember(this.state.filterText);
        //     }
        // }
        if (!newMember) return this.state.targets;

        const newTargets = [...(this.state.targets || []), newMember];
        this.setState({ targets: newTargets, filterText: "" });
        return newTargets;
    }

    /**
     * Check if there are unknown profiles if promptBeforeInviteUnknownUsers setting is enabled.
     * If so show the "invite anyway?" dialog. Otherwise directly create the DM local room.
     */
    private checkProfileAndStartDm = async (): Promise<void> => {
        this.setBusy(true);
        // const targets = this.convertFilter();
        // if (SettingsStore.getValue("promptBeforeInviteUnknownUsers")) {
        //     const unknownProfileUsers = await extractTargetUnknownProfiles(targets, this.profilesStore);

        //     if (unknownProfileUsers.length) {
        //         this.setBusy(false);
        //         this.showAskInviteAnywayDialog(unknownProfileUsers);
        //         return;
        //     }
        // }

        await this.startDm();
    };

    /**
     * Avvia la chat singola
     */
    private startDm = async (): Promise<void> => {
        this.setBusy(true);
        try {
            const cli = MatrixClientPeg.safeGet();
            const targets = this.convertFilter();
            await startDmOnFirstMessage(cli, targets);
            this.props.onFinished(true);
        } catch (err) {
            logger.error(err);
            this.setState({
                busy: false,
                errorText: _t("invite.error_dm"),
            });
        }
    };

    private setBusy(busy: boolean): void {
        this.setState({
            busy,
        });
    }

    /**
     * Chiede se invitare comunque nel caso l'utente non è verificato
     * @param unknownProfileUsers 
     */
    // private showAskInviteAnywayDialog(unknownProfileUsers: { userId: string; errorText: string }[]): void {
    //     Modal.createDialog(AskInviteAnywayDialog, {
    //         unknownProfileUsers,
    //         onInviteAnyways: () => this.startDm(),
    //         onGiveUp: () => {
    //             this.setBusy(false);
    //         },
    //         description: _t("invite.ask_anyway_description"),
    //         inviteNeverWarnLabel: _t("invite.ask_anyway_never_warn_label"),
    //         inviteLabel: _t("invite.ask_anyway_label"),
    //     });
    // }

    /**
     * Invito nel gruppo
     * @returns 
     */
    private inviteUsers = async (): Promise<void> => {
        if (this.props.kind !== InviteKind.Invite) return;
        this.setState({ busy: true });
        this.convertFilter();
        const targets = this.convertFilter();
        const targetIds = targets.map((t) => t.userId);

        const cli = MatrixClientPeg.safeGet();
        const room = cli.getRoom(this.props.roomId);
        if (!room) {
            logger.error("Failed to find the room to invite users to");
            this.setState({
                busy: false,
                errorText: _t("invite.error_find_room"),
            });
            return;
        }

        try {
            const result = await inviteMultipleToRoom(cli, this.props.roomId, targetIds, {
                // We show our own progress body, so don't pop up a separate dialog.
                inhibitProgressDialog: true,
            });
            if (!this.shouldAbortAfterInviteError(result, room)) {
                // handles setting error message too
                this.props.onFinished(true);
            }
        } catch (err) {
            logger.error(err);
            this.setState({
                busy: false,
                errorText: _t("invite.error_invite"),
            });
        }
    };

    // private get screenName(): ScreenName | undefined {
    //     switch (this.props.kind) {
    //         case InviteKind.Dm:
    //             return "StartChat";
    //         default:
    //             return undefined;
    //     }
    // }

    /**
     * If encryption by default is enabled, third-party invites should be encrypted as well.
     * For encryption to work, the other side requires a device.
     * To achieve this Element implements a waiting room until all have joined.
     * Waiting for many users degrades the UX → only one email invite is allowed at a time.
     *
     * @param targets - Optional member list to check. Uses targets from state if not provided.
     */
    private canInviteMore(targets?: (Member | RoomMember)[]): boolean {
        targets = targets || this.state.targets;
        return this.props.kind !== InviteKind.Dm || targets.length === 0 || !this.encryptionByDefault; //|| !targets.some((t) => t instanceof ThreepidMember);
    }


    private hasSelection(): boolean {
        return this.state.targets.length > 0 || (!!this.state.filterText && this.state.filterText.includes("@"));
    }

    /**
     * Render content of the common "users" tab that is shown whether we have a regular invite dialog or a
     * "CallTransfer" one.
     */
    private renderMainTab(): JSX.Element {
        let goButtonFn: (() => Promise<void>);
        let roomId: string | undefined = undefined;
        const isInvite = this.props.kind === InviteKind.Invite;
        let buttonText = "";
        if (isInvite) {
            roomId = this.props.roomId;
            buttonText = _t("action.add_participants")
            goButtonFn = this.inviteUsers;
        } else {
            goButtonFn = this.checkProfileAndStartDm;
            buttonText = _t("invite.start_chat");
        }

        return (
            <React.Fragment>
                {this.state.busy ? <InviteProgressBody /> : <ContactList
                    onTargetsChange={this.onTargetsChange}
                    busy={this.state.busy}
                    roomId={roomId}
                    allowMultiple={isInvite}
                // showToggle={isInvite}
                />}
                <div className="mx_CreateRoomDialog_buttonsWrap">
                    <DialogButtons
                        primaryButton={buttonText}
                        primaryDisabled={this.state.busy || !this.hasSelection()}
                        onPrimaryButtonClick={(ev) => {
                            ev.preventDefault();
                            void goButtonFn(); // esegue la funzione asincrona, ignora la Promise
                        }}
                        onCancel={this.onCancel}
                    />
                </div>
            </React.Fragment>
        );
    }

    private onCancel = (): void => {
        this.props.onFinished(false);
    };

    private onTargetsChange = (targets: Member[]): void => {
        this.setState({ targets: targets });
    };

    /**
     * Render the complete dialog
     */
    public render(): React.ReactNode {
        let title;
        if (this.props.kind === InviteKind.Dm) {
            title = _t("space.add_existing_room_space.dm_heading");
        } else if (this.props.kind === InviteKind.Invite) {
            const roomId = this.props.roomId;
            const room = MatrixClientPeg.get()?.getRoom(roomId);
            const roomName = room ? getRoomDisplayName(room) : _t("common.unnamed_room");
            title = _t("invite.to_room", {
                roomName: roomName,
            });
        }

        return (
            <BaseDialog
                className="mx_InviteDialog_other"
                hasCancel={true}
                onFinished={this.props.onFinished}
                title={title}                
            // screenName={this.screenName}
            >
                <div className="mx_InviteDialog_content">{this.renderMainTab()}</div>
            </BaseDialog>
        );
    }
}
